import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Sparkles, Globe, Mic, Zap, Heart, Award } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />

      <section className="relative border-b border-black/10">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">
            About · Jingle Hooks
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-balance">
            We believe every brand deserves a{" "}
            <span className="text-[#FF4500]">song</span> that sells.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-zinc-600 max-w-3xl leading-relaxed">
            Jingle Hooks is India's first AI jingle maker built for the speed of performance marketing. Paste a script, pick a genre, and walk away with a production-ready jingle in 90 seconds — ready for reels, TV spots, radio, and point-of-sale campaigns.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-black/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">Our Story</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter leading-tight mb-6">
              From studio price-tags to 90-second jingles.
            </h2>
            <div className="space-y-4 text-zinc-700 leading-relaxed">
              <p>
                A traditional jingle costs ₹50,000–₹5,00,000 and takes 2–6 weeks. By then, your Diwali sale is over, the reel trend has died, and your marketing budget is burning faster than your creative pipeline can fill it.
              </p>
              <p>
                We built Jingle Hooks because Indian D2C brands, local shopkeepers, and performance marketers deserve better. Our AI composes real songs — not voice-overs — with vocals, melody, and instrumentation, in 11 Indian languages, for a fraction of the cost.
              </p>
              <p>
                Your first jingle is free. Your next thousand will cost you less than one traditional studio booking.
              </p>
            </div>
          </div>

          <div className="bg-[#F8F9FA] border border-black/10 p-8">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-6">By the numbers</div>
            <div className="space-y-6">
              <div>
                <div className="font-display text-5xl font-semibold tabular-nums">90s</div>
                <div className="text-sm text-zinc-500 mt-1">Average generation time</div>
              </div>
              <div>
                <div className="font-display text-5xl font-semibold tabular-nums">11</div>
                <div className="text-sm text-zinc-500 mt-1">Indian languages supported</div>
              </div>
              <div>
                <div className="font-display text-5xl font-semibold tabular-nums">16 × 12</div>
                <div className="text-sm text-zinc-500 mt-1">Genre × mood combinations</div>
              </div>
              <div>
                <div className="font-display text-5xl font-semibold tabular-nums">2,400+</div>
                <div className="text-sm text-zinc-500 mt-1">Jingles generated this month</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">What we believe</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter leading-tight mb-12">
            Four non-negotiables that guide everything we ship.
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: Zap, t: "Speed over perfection", d: "A ₹10,000 jingle that takes 6 weeks is useless if your campaign launches next Monday. We optimise for under-90-second turnaround, because the best jingle is the one that ships." },
              { icon: Globe, t: "India-first, always", d: "Hindi, Punjabi, Tamil, Bengali, Marathi, Gujarati — the model is trained to respect regional phrasing, culture, and musical traditions. Bollywood dhol, Punjabi tumbi, Carnatic undertones — all one click away." },
              { icon: Mic, t: "Real music, not voice-overs", d: "ElevenLabs' frontier music model generates full songs with vocals, melody, and instrumentation — not robotic TTS stitched over a backing track. Your jingles sound like jingles, not announcements." },
              { icon: Heart, t: "Fair, transparent pricing", d: "1 credit = 1 second of music. No hidden renewal gotchas, no tiered feature gates on languages, and unused credits in Pro plans roll over forever. Simple." },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="bg-white border border-black/10 p-7 card-hover">
                  <div className="w-12 h-12 border border-black/10 flex items-center justify-center mb-5 group-hover:bg-[#FF4500] group-hover:text-white transition-colors">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-2">{v.t}</h3>
                  <p className="text-zinc-600 leading-relaxed text-sm">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#09090B] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">
            <Award size={16} className="inline mr-1" /> Our promise
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tighter leading-[0.95] text-balance max-w-4xl mx-auto">
            Your next viral ad is <span className="text-[#FF4500]">90 seconds</span> away.
          </h2>
          <p className="mt-6 text-zinc-400 text-lg max-w-2xl mx-auto">
            Start free. No credit card. Two free jingles on the house — one for the idea, one for the edit.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/login">
              <Button className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-12 px-6 text-base font-semibold" data-testid="about-cta-btn">
                Generate Your First Free Jingle
                <Sparkles size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" className="text-sm font-semibold underline underline-offset-4 hover:text-[#FF4500]">
              See pricing →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
